import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonToggleModule } from '@angular/material/button-toggle';

import { FormDialogShellComponent } from '@shared/components/form-dialog/form-dialog-shell.component';
import { AutoSelectComponent } from '@app/shared/components/auto-select/auto-select.component';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
import { LogisticsCatalogs, TransferRequestsService, errMsg } from '../logistics.service';

/** canCrossHospital: mesa de control I/II y administradores (el API lo vuelve a validar). */
export interface TransferRequestFormData { scopePlaces: string[] | null; canCrossHospital?: boolean; }
type Direction = 'IN_TO_LOCKER' | 'LOCKER_TO_IN' | 'HOSPITAL_TO_HOSPITAL';

/**
 * Nueva solicitud de traspaso: in-house → locker, locker → in-house (mismo
 * hospital) o hospital → hospital, de locker a locker (solo mesa de control y
 * administradores). Quien la registra queda como "Solicita" y ya no se edita.
 */
@Component({
  selector: 'app-transfer-request-form-dialog',
  standalone: true,
  imports: [FormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule,
    MatTooltipModule, MatDatepickerModule, MatButtonToggleModule, FormDialogShellComponent, AutoSelectComponent],
  styleUrl: '../logistics-dialog.scss',
  styles: [`
    .dir { grid-column: 1 / -1; display: flex; flex-wrap: wrap; align-items: center; gap: 8px 14px; }
    .dir span { font-size: 13px; color: #555; }
  `],
  template: `
<app-form-dialog-shell icon="swap_horiz" heading="Nueva solicitud de traspaso"
  [saving]="saving()" saveLabel="Registrar solicitud" saveIcon="send"
  formId="transfer-request-form" (cancelled)="ref.close(false)">
<form id="transfer-request-form" (ngSubmit)="save()">
  <div class="form-grid">
    <mat-form-field appearance="outline" class="full-col">
      <mat-label>Solicita</mat-label>
      <input matInput name="requestedBy" [value]="requestedBy" readonly>
      <mat-icon matSuffix>person</mat-icon>
    </mat-form-field>

    @if (!(isHospital && place)) {
      <app-auto-select class="full-col" [label]="isHospital ? 'Hospital origen' : 'Hospital'" name="place" [(ngModel)]="place" (changed)="onPlace()"
        [options]="placeOptions" [labelFn]="placeLabel"></app-auto-select>
    }

    <div class="dir">
      <span>Dirección:</span>
      <mat-button-toggle-group name="direction" [(ngModel)]="direction" (change)="onDirection()" [hideSingleSelectionIndicator]="true" aria-label="Dirección del traspaso">
        <mat-button-toggle value="IN_TO_LOCKER">In-house → Locker</mat-button-toggle>
        <mat-button-toggle value="LOCKER_TO_IN">Locker → In-house</mat-button-toggle>
        @if (data.canCrossHospital) {
          <mat-button-toggle value="HOSPITAL_TO_HOSPITAL">Hospital → Hospital</mat-button-toggle>
        }
      </mat-button-toggle-group>
    </div>

    @if (isHospital) {
      @if (place) {
        <!-- Hospital origen: el que ya se eligió; no se cambia aquí. -->
        <mat-form-field appearance="outline">
          <mat-label>Hospital origen</mat-label>
          <input matInput name="originPlace" [value]="placeName(place)" readonly>
          <mat-icon matSuffix>lock</mat-icon>
        </mat-form-field>
      }
      <app-auto-select label="Hospital destino" name="toPlace" [(ngModel)]="toPlace"
        [options]="toPlaceOptions" [labelFn]="placeLabel"></app-auto-select>
      <p class="hint-box full-col"><mat-icon>info</mat-icon>
        <span>Los lockers de origen y destino se eligen al atender la solicitud en Traspasos.</span></p>
    } @else {
      <app-auto-select [label]="direction === 'IN_TO_LOCKER' ? 'Sale del in-house' : 'Sale del locker'" name="from"
        [(ngModel)]="fromWarehouse" (changed)="onFrom()" [options]="fromOptions" [labelFn]="whLabel"></app-auto-select>
      <app-auto-select [label]="direction === 'LOCKER_TO_IN' ? 'Entra al in-house' : 'Entra al locker'" name="to"
        [(ngModel)]="toWarehouse" [options]="toOptions" [labelFn]="whLabel"></app-auto-select>
    }

    <mat-form-field appearance="outline">
      <mat-label>Se necesita para</mat-label>
      <input matInput [matDatepicker]="dp" name="neededBy" [(ngModel)]="neededBy" [min]="today">
      <mat-datepicker-toggle matSuffix [for]="dp"></mat-datepicker-toggle>
      <mat-datepicker #dp></mat-datepicker>
    </mat-form-field>

    <div class="section-title">
      <mat-icon>inventory_2</mat-icon> Material solicitado
      <span class="spacer"></span>
      <button mat-stroked-button type="button" (click)="addLine()"><mat-icon>add</mat-icon> Agregar código</button>
    </div>
    <div class="lines">
      @for (ln of lines; track ln.key; let i = $index) {
        <div class="line-row">
          <app-auto-select [label]="'Código ' + (i + 1)" [name]="'p' + ln.key" [(ngModel)]="ln.product"
            [options]="cat.products" [labelFn]="cat.productLabel"></app-auto-select>
          <mat-form-field appearance="outline">
            <mat-label>Piezas</mat-label>
            <input matInput type="number" min="1" step="1" [name]="'q' + ln.key" [(ngModel)]="ln.quantity">
          </mat-form-field>
          <button mat-icon-button type="button" color="warn" matTooltip="Quitar" (click)="lines.splice(i, 1)" [disabled]="lines.length === 1">
            <mat-icon>delete</mat-icon>
          </button>
        </div>
      }
    </div>

    <mat-form-field appearance="outline" class="full-col">
      <mat-label>Notas</mat-label>
      <textarea matInput rows="2" name="notes" [(ngModel)]="notes" maxlength="1000"></textarea>
    </mat-form-field>

    <p class="hint-box full-col"><mat-icon>info</mat-icon>
      <span>Una vez registrada ya no se puede editar. Se atiende desde Traspasos.</span></p>
  </div>
</form>
</app-form-dialog-shell>
  `,
})
export class TransferRequestFormDialogComponent {
  readonly data = inject<TransferRequestFormData>(MAT_DIALOG_DATA);
  readonly ref = inject(MatDialogRef<TransferRequestFormDialogComponent>);
  readonly cat = inject(LogisticsCatalogs);
  private readonly service = inject(TransferRequestsService);
  private readonly msg = inject(CustomMessageService);

  readonly saving = signal(false);
  readonly today = new Date(new Date().setHours(0, 0, 0, 0));
  readonly requestedBy = sessionStorage.getItem('userName') || sessionStorage.getItem('userEmail') || '';
  readonly placeLabel = (p: any): string => String(p?.alias || p?.name || '');
  readonly whLabel = (w: any): string => String(w?.name || '');

  place: string | null = null;
  direction: Direction = 'IN_TO_LOCKER';
  toPlace: string | null = null;
  fromWarehouse: string | null = null;
  toWarehouse: string | null = null;
  neededBy: Date | null = null;
  notes = '';
  private seq = 0;
  lines: { key: number; product: string | null; quantity: number }[] = [];

  readonly placeOptions = this.cat.places
    .filter(p => (!this.data.scopePlaces || this.data.scopePlaces.includes(String(p.id))) &&
      this.cat.sourceKind({ place: p.id }) === 'HOSPITAL')
    .sort((a, b) => this.placeLabel(a).localeCompare(this.placeLabel(b)));

  get isHospital(): boolean { return this.direction === 'HOSPITAL_TO_HOSPITAL'; }
  placeName(id: string | null): string { return this.placeLabel(this.cat.places.find(p => p.id === id)); }

  /** Hospitales destino (Hospital → Hospital): todos menos el de origen. */
  get toPlaceOptions(): any[] { return this.placeOptions.filter(p => p.id !== this.place); }

  private ofPlace(place: string | null, inhouse: boolean): any[] {
    return this.cat.warehouses.filter(w => w.place === place && this.cat.isInhouse(w) === inhouse)
      .sort((a, b) => this.whLabel(a).localeCompare(this.whLabel(b)));
  }
  get fromOptions(): any[] { return this.ofPlace(this.place, this.direction === 'IN_TO_LOCKER'); }
  get toOptions(): any[] {
    if (this.direction === 'HOSPITAL_TO_HOSPITAL') return this.ofPlace(this.toPlace, false);
    return this.ofPlace(this.place, this.direction === 'LOCKER_TO_IN');
  }

  constructor() {
    this.addLine();
    if (this.placeOptions.length === 1) { this.place = this.placeOptions[0].id; this.onPlace(); }
  }

  addLine(): void { this.lines.push({ key: ++this.seq, product: null, quantity: 1 }); }

  onPlace(): void {
    this.fromWarehouse = this.toWarehouse = null;
    if (this.toPlace === this.place) this.toPlace = null;
    this.pickSingles();
  }
  onDirection(): void { this.fromWarehouse = this.toWarehouse = null; this.toPlace = null; this.pickSingles(); }
  onFrom(): void { if (this.toWarehouse === this.fromWarehouse) this.toWarehouse = null; if (!this.toWarehouse) this.pickSingles(); }
  onToPlace(): void { this.toWarehouse = null; this.pickSingles(); }
  private pickSingles(): void {
    if (this.fromOptions.length === 1) this.fromWarehouse = this.fromOptions[0].id;
    if (this.toOptions.length === 1) this.toWarehouse = this.toOptions[0].id;
  }

  async save(): Promise<void> {
    const lines = this.lines.filter(l => l.product).map(l => {
      const p = this.cat.productsById[l.product!];
      return { product: l.product, code: p?.code ?? '', description: p?.description ?? '',
               quantity: Math.max(1, Math.floor(Number(l.quantity) || 1)), supplied: 0 };
    });
    const problem = !this.place ? 'Elige el hospital.'
      : this.direction === 'HOSPITAL_TO_HOSPITAL' && !this.toPlace ? 'Elige el hospital destino.'
      : !this.isHospital && (!this.fromWarehouse || !this.toWarehouse) ? 'Elige de dónde sale y a dónde entra el material.'
      : !lines.length ? 'Agrega al menos un código.'
      : new Set(lines.map(l => l.product)).size !== lines.length ? 'Hay códigos repetidos: junta las piezas en un solo renglón.'
      : null;
    if (problem) { await this.msg.open({ title: 'Revisa la solicitud', type: 'warning', message: problem, dismissBtn: true }); return; }

    this.saving.set(true);
    try {
      const r: any = await this.service.create({
        place: this.place, direction: this.direction,
        toPlace: this.direction === 'HOSPITAL_TO_HOSPITAL' ? this.toPlace : this.place,
        fromWarehouse: this.isHospital ? null : this.fromWarehouse, toWarehouse: this.isHospital ? null : this.toWarehouse,
        neededBy: this.neededBy ? localDay(this.neededBy) : null, lines, notes: this.notes.trim() || null, status: 'Nueva',
      });
      const folio = r?.data?.[0]?.folio ?? '';
      await this.msg.open({ title: '¡Registrada!', type: 'success', message: `Solicitud de traspaso <b>${folio}</b> registrada.`, duration: 2600 });
      this.ref.close(true);
    } catch (err) {
      await this.msg.open({ title: 'Error', type: 'error', message: errMsg(err, 'No se pudo guardar la solicitud.'), dismissBtn: true });
    } finally {
      this.saving.set(false);
    }
  }
}

function localDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
